import React, { useRef } from "react";
import { Modal } from "antd";
import { FilePdfOutlined } from "@ant-design/icons";
import { ButtonItem } from "../Items";
import { extraerLineasDePdf } from "./extraerLineasDePdf";
import { parsearLineasNadro } from "./Nadro";
import { parsearLineasRosario } from "./Rosario";
import { parsearLineasLevic } from "./Levic";
import { parsearLineasFanasa } from "./Fanasa";
import { parsearLineasGenerico } from "./Generico";
import { parsearLineasPisa } from "./Pisa";
import { parsearLineasSalubox } from "./Salubox";
import { parsearLineasSabela } from "./Sabela";
import { parsearLineasFacturaGenerica } from "./FacturaGenerica";
import { aplicarLotesAlForm } from "./aplicarLotesAlForm";

// Mapa de RFC -> { label, parsear }
// Para agregar un nuevo proveedor: añadir su RFC aquí y crear su archivo de parser.
const PROVEEDORES_PDF = {
  'NAD901023GRA': {
    label: 'Leer PDF Nadro',
    parsear: parsearLineasNadro,
  },
  'MEMR531218RH2': {
    label: 'Leer PDF Rosario',
    parsear: parsearLineasRosario,
  },
  'DLE040628S64': {
    label: 'Leer PDF Levic',
    parsear: parsearLineasLevic,
  },
  'FNA951220DA9': {
    label: 'Leer PDF Fanasa',
    parsear: parsearLineasFanasa,
  },
  'LPI830527KJ2': {
    label: 'Leer PDF PISA',
    parsear: parsearLineasPisa,
  },
  'SAL1508035B3': {
    label: 'Leer PDF Salubox',
    parsear: parsearLineasSalubox,
  },
  'HECJ870814GCA': {
    label: 'Leer PDF Sabela',
    parsear: parsearLineasSabela,
  },
};

// Fallback para RFCs sin parser específico: usa el formato de remisión propio
const PROVEEDOR_GENERICO = {
  label: 'Leer PDF',
  parsear: parsearLineasGenerico,
};

/**
 * Si el parser del proveedor no reconoció nada, reintenta con el parser genérico de factura,
 * que trabaja por señales (caducidad + lote + aritmética del renglón) en vez de por maqueta.
 * Ese caso hoy termina igual en el modal de "no se encontraron coincidencias", así que el
 * respaldo no puede dejar al usuario peor de lo que estaba; lo que proponga lo revisa él.
 */
function parsearConRespaldo(parsear, allLines) {
  const resultado = parsear(allLines);
  if (resultado && resultado.size > 0) return resultado;
  return parsearLineasFacturaGenerica(allLines);
}

// Volcado de las filas reconstruidas, para afinar el parser de un proveedor nuevo sin
// adivinar cómo agrupó pdfjs las columnas: localStorage.setItem('debugLeerPdf','1')
function volcarLineas(allLines) {
  try {
    if (window.localStorage?.getItem('debugLeerPdf') === '1') {
      console.log('[LeerPdf] filas del PDF:', allLines);
    }
  } catch (err) {
    // localStorage puede no estar disponible (modo privado); el volcado es opcional
  }
}

/**
 * Componente genérico para leer PDFs de proveedores.
 *
 * Props:
 *   rfcProveedor   string   RFC del proveedor activo
 *   formRef        ref      Referencia al Form de antd (para leer/setear insumos)
 *   todasInsumos   array    Lista de insumos con _id y descripcion
 *   onLoadingChange fn      Callback (boolean) para manejar el estado loading en el padre
 */
function LeerPdf({ rfcProveedor, formRef, todasInsumos, onLoadingChange }) {
  const pdfInputRef = useRef(null);
  if (!rfcProveedor) return null;
  const proveedor = PROVEEDORES_PDF[rfcProveedor] || PROVEEDOR_GENERICO;

  const handleClick = () => {
    pdfInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';

    onLoadingChange?.(true);
    try {
      const allLines = await extraerLineasDePdf(file);
      volcarLineas(allLines);
      const productsByDesc = parsearConRespaldo(proveedor.parsear, allLines);
      aplicarResultadosAlForm(formRef, todasInsumos, productsByDesc);
    } catch (err) {
      console.error('Error al parsear PDF:', err);
      Modal.error({ title: 'Error al leer el PDF', centered: true });
    }
    onLoadingChange?.(false);
  };

  return (
    <>
      <ButtonItem icon={<FilePdfOutlined />} onClick={handleClick}>
        {proveedor.label}
      </ButtonItem>
      <input
        type="file"
        ref={pdfInputRef}
        accept=".pdf"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </>
  );
}

function aplicarResultadosAlForm(formRef, todasInsumos, productsByDesc) {
  const { matched, total } = aplicarLotesAlForm(formRef, todasInsumos, productsByDesc);

  if (matched > 0) {
    Modal.success({ title: `Se prellenaron lotes para ${matched} insumo(s) de ${total} productos en el listado.`, content: 'Favor de verificar.', centered: true });
  } else {
    Modal.warning({ title: 'No se encontraron coincidencias en el PDF con los insumos de la factura', content: 'Recomendación: leer primero la factura (XML) para que los insumos empaten al leer el PDF.', centered: true });
  }
}

export default LeerPdf;
